'use client';

import React from 'react';
import { animate, useInView, useReducedMotion } from 'framer-motion';

// ─────────────────────────────────────────────────────────────────────────────
// Shared primitives for the Course Gallery page sections.
// Design scale: 1rem = 8px (root font-size is fluid — see app/globals.scss).
// Figma: WCE | New UI → "Course Page" → Gallery / Course (3780:19178).
//
// SVG path data below is copied verbatim from the Figma exports; the multi-path
// Material Symbols icons ship as files in /public/courseGallery instead.
// ─────────────────────────────────────────────────────────────────────────────

/** Decorative "||| LABEL |||" kicker — JetBrains Mono, uppercase, graduated
 *  pipe opacity. `color` lets the white Master Class section use black pipes. */
export function PipeChip({
  children,
  color = '#b8d2e2',
  className = '',
  hideRight = false,
}: {
  children: React.ReactNode;
  color?: string;
  className?: string;
  hideRight?: boolean;
}) {
  return (
    <p
      className={`m-0 font-jetbrainsmono uppercase text-[1.5rem] lg:text-[1.75rem] ${className}`}
      style={{ color }}
    >
      <span style={{ color, opacity: 0.2 }}>|</span>
      <span style={{ color, opacity: 0.4 }}>|</span>
      <span style={{ color, opacity: 0.6 }}>|</span>
      <span style={{ color, opacity: 0.8 }}> {children} </span>
      {!hideRight && (
        <>
          <span style={{ color, opacity: 0.6 }}>|</span>
          <span style={{ color, opacity: 0.4 }}>|</span>
          <span style={{ color, opacity: 0.2 }}>|</span>
        </>
      )}
    </p>
  );
}

/** Progress-style pipe run used on the Master Class cards: `filled` of `total`
 *  pipes at full strength, the remainder at 20% (Figma 3780:19278 → 3780:19313). */
export function PipeMeter({
  filled,
  total = 6,
  color = '#000000',
  className = '',
}: {
  filled: number;
  total?: number;
  color?: string;
  className?: string;
}) {
  return (
    <p
      className={`m-0 font-jetbrainsmono uppercase whitespace-nowrap text-[1.5rem] lg:text-[1.75rem] ${className}`}
      aria-hidden
    >
      <span style={{ color }}>{'|'.repeat(Math.max(0, Math.min(filled, total)))}</span>
      <span style={{ color, opacity: 0.2 }}>
        {'|'.repeat(Math.max(0, total - filled))}
      </span>
    </p>
  );
}

/** 4px separator dot between the meta items on a discipline card. */
export const MetaDot = () => (
  <span
    aria-hidden
    className="rounded-full shrink-0 bg-white/60 w-[0.5rem] h-[0.5rem]"
  />
);

/** Checkmark in the discipline bullet rows (Figma 3780:19482). */
export const CheckIcon = ({ className = '' }: { className?: string }) => (
  <svg
    viewBox="0 0 15 10.2587"
    fill="none"
    className={`shrink-0 ${className}`}
    aria-hidden
  >
    <path
      d="M5.625 10.2587L0 4.63375L0.88375 3.75L5.625 8.49062L14.1163 0L15 0.88375L5.625 10.2587Z"
      fill="currentColor"
    />
  </svg>
);

/** Splits a stat value into a fixed prefix, an animatable number and a fixed
 *  suffix — "$0" → ["$", 0, ""], "85+" → ["", 85, "+"], "9" → ["", 9, ""].
 *  Returns null when there is no number to count (so the value renders as-is). */
export function parseStatValue(
  value: string,
): {
  prefix: string;
  target: number;
  suffix: string;
  decimals: number;
  useGrouping: boolean;
} | null {
  const match = value.match(/^(\D*?)(\d[\d,]*(?:\.\d+)?)(.*)$/s);
  if (!match) return null;

  const [, prefix, digits, suffix] = match;
  const normalised = digits.replaceAll(",", "");
  const target = Number(normalised);
  if (!Number.isFinite(target)) return null;

  const decimals = normalised.includes(".")
    ? normalised.split(".")[1].length
    : 0;

  return {
    prefix,
    target,
    suffix,
    decimals,
    useGrouping: digits.includes(","),
  };
}

/** Counts a stat number up from zero the first time it scrolls into view.
 *  Non-numeric values, a zero target and reduced-motion preferences all fall
 *  straight through to the final text. */
export function CountUpValue({
  value,
  duration = 1.6,
  className = '',
}: {
  value: string;
  duration?: number;
  className?: string;
}) {
  const ref = React.useRef<HTMLSpanElement>(null);
  const inView = useInView(ref, { once: true, amount: 0.6 });
  const reduce = useReducedMotion();

  const parsed = parseStatValue(value);
  const [display, setDisplay] = React.useState<string | null>(
    parsed ? formatCount(0, parsed.decimals, parsed.useGrouping) : null,
  );

  React.useEffect(() => {
    if (!parsed || !inView) return;

    // Nothing to animate — snap to the final number.
    if (reduce || parsed.target === 0) {
      setDisplay(
        formatCount(parsed.target, parsed.decimals, parsed.useGrouping),
      );
      return;
    }

    const controls = animate(0, parsed.target, {
      duration,
      ease: [0.15, 0, 0.55, 1], // matches FADE_EASE used site-wide
      onUpdate: (latest) =>
        setDisplay(formatCount(latest, parsed.decimals, parsed.useGrouping)),
    });

    return () => controls.stop();
    // `parsed` is derived from `value`, so keying on the raw string is enough.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, inView, reduce, duration]);

  if (!parsed) {
    return <span className={className}>{value}</span>;
  }

  return (
    <span ref={ref} className={className}>
      {parsed.prefix}
      {display ?? formatCount(0, parsed.decimals, parsed.useGrouping)}
      {parsed.suffix}
    </span>
  );
}

function formatCount(n: number, decimals: number, useGrouping: boolean) {
  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
    useGrouping,
  }).format(n);
}

/** Chevron used by the All Classes filter and the discipline pager (3780:19618). */
export const ChevronIcon = ({ className = '' }: { className?: string }) => (
  <svg
    viewBox="0 0 15 8.55"
    fill="none"
    className={`shrink-0 ${className}`}
    aria-hidden
  >
    <path
      d="M7.5 8.55L0 1.05L1.05 0L7.5 6.45L13.95 0L15 1.05L7.5 8.55Z"
      fill="currentColor"
    />
  </svg>
);
